import WorkingFileUploader from '@/components/AdvancedUpload';
import BatchList from '@/components/BatchList';
import FileList from '@/components/FileList';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { execute } from '@/graphql/execute';
import { ProcessJobType, Project } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useSubscription } from '@/hooks/useSubscription';
import { useUpload } from '@/hooks/useUpload';
import PageSkeleton from '@/layout/Skeleton';
import { analytics } from '@/lib/analytics';
import { completeBatch, startBatch, startBatchProcessing } from '@/queries/attachment.query';
import { DangerTriangle, HardDrive } from '@mynaui/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FunctionComponent, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router';
import { toast } from 'sonner';

interface iindexProps {}

const formatBytes = (bytes: number, k: number = 1024): string => {
  if (bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const index: FunctionComponent<iindexProps> = () => {
  const { isUploading, cancelUpload, startUploads, files, addFiles, removeFile, clearFiles, storageInfo } = useUpload();
  const { organization } = useOrganizations();
  const { initialLoadComplete } = useSubscription();
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importType, setImportType] = useState<'WMS' | 'POTREE'>('WMS');
  const [batchLabel, setBatchLabel] = useState('');
  const { t } = useTranslation('project');

  const importMutation = useMutation({
    mutationFn: async (url: string) => {
      const batchResult = (await execute(startBatch, {
        project: projectId!,
        numberOfAttachments: 0,
      })) as any;
      const newBatchId = batchResult?.batch?.id;
      if (!newBatchId) throw new Error('Failed to create batch');

      await execute(completeBatch, { id: newBatchId });

      await execute(startBatchProcessing, {
        id: newBatchId,
        srid: 4326,
        type: importType as ProcessJobType,
        importUrl: url,
      });
    },
    onSuccess: () => {
      analytics.capture('Import Started', { type: importType });
      setShowImportDialog(false);
      setImportUrl('');
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success(t('upload.importStarted', { type: importType }));
    },
    onError: () => {
      toast.error(t('upload.failedImport', { type: importType }));
    },
  });

  const { project, projectLoading, href } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();

  usePageMeta({ title: [t('upload.title'), project?.name] });

  // Calculate E57 file limits
  const e57Warning = useMemo(() => {
    const e57Files = files.filter((f) => f.name.toLowerCase().endsWith('.e57'));
    const e57Count = e57Files.length;
    const e57TotalBytes = e57Files.reduce((sum, f) => sum + f.file.size, 0);
    const MAX_E57_COUNT = 32;
    const MAX_E57_BYTES = 100 * 1024 * 1024 * 1024; // 100 GB
    const exceedsCount = e57Count > MAX_E57_COUNT;
    const exceedsSize = e57TotalBytes > MAX_E57_BYTES;
    return { exceedsCount, exceedsSize, e57Count, e57TotalBytes, show: exceedsCount || exceedsSize };
  }, [files]);

  // Calculate storage status
  const storageStatus = useMemo(() => {
    const storageUsedBytes = organization?.storageUsedBytes ?? 0;
    const storageLimitBytes = storageInfo.storageLimitBytes;
    const pendingBytes = storageInfo.pendingUploadBytes;
    const totalAfterUpload = storageUsedBytes + pendingBytes;
    const wouldExceedLimit = totalAfterUpload > storageLimitBytes;
    const usagePercent = Math.min((storageUsedBytes / storageLimitBytes) * 100, 100);
    const projectedPercent = Math.min((totalAfterUpload / storageLimitBytes) * 100, 100);
    const remainingBytes = Math.max(storageLimitBytes - storageUsedBytes, 0);

    return {
      storageUsedBytes,
      storageLimitBytes,
      pendingBytes,
      totalAfterUpload,
      wouldExceedLimit,
      usagePercent,
      projectedPercent,
      remainingBytes,
    };
  }, [organization?.storageUsedBytes, storageInfo.storageLimitBytes, storageInfo.pendingUploadBytes]);

  return (
    <PageSkeleton
      steps={
        projectLoading
          ? [{ label: t('edit.loading'), href }]
          : [
              { label: project.name, href },
              {
                label: t('upload.title'),
                href: `${href}/upload`,
              },
            ]
      }>
      <div className='max-w-screen-md px-4 mx-auto w-full pb-12'>
        <div id='onboarding-upload' className='flex items-center justify-between mt-12 mb-1'>
          <h1 className='text-xl font-semibold'>{t('upload.uploadFiles')}</h1>
          <Button variant='outline' onClick={() => setShowImportDialog(true)}>
            {t('upload.importUrl')}
          </Button>
        </div>
        <p className='text-sm text-gray-500 dark:text-stone-500 mb-4'>{t('upload.uploadDescription')}</p>

        {showImportDialog && (
          <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
            <div className='bg-white dark:bg-stone-900 rounded-lg p-6 w-[420px] shadow-lg'>
              <h3 className='text-lg font-semibold mb-4'>{t('upload.importUrl')}</h3>
              <div className='flex flex-col gap-3'>
                <div className='flex items-start gap-1 flex-col'>
                  <Label>{t('upload.importType')}</Label>
                  <Select value={importType} onValueChange={(v) => setImportType(v as 'WMS')}>
                    <SelectTrigger className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='WMS'>WMS</SelectItem>
                      <SelectItem value='POTREE'>Potree</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {importType === 'POTREE' && (
                  <Alert>
                    <DangerTriangle className='h-4 w-4' />
                    <AlertDescription>{t('upload.potreeWarning')}</AlertDescription>
                  </Alert>
                )}
                <div className='flex items-start gap-1 flex-col'>
                  <Label>{t('upload.url')}</Label>
                  <Input
                    placeholder={
                      importType === 'POTREE' ? 'https://example.com/potree/metadata.json' : 'https://example.com/wms'
                    }
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                  />
                </div>
                <div className='flex gap-2 justify-end mt-2'>
                  <Button variant='outline' onClick={() => setShowImportDialog(false)}>
                    {t('upload.cancel')}
                  </Button>
                  <Button
                    disabled={!importUrl || importMutation.isPending}
                    onClick={() => importMutation.mutate(importUrl)}>
                    {t('upload.import')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Storage Usage Bar */}
        <div className='mb-6 p-4 bg-slate-50 dark:bg-stone-900 rounded-lg'>
          <div className='flex items-center gap-2 mb-2'>
            <HardDrive className='h-4 w-4 text-slate-500 dark:text-stone-500' />
            <span className='text-sm font-medium text-slate-700 dark:text-stone-300'>{t('upload.storage')}</span>
          </div>
          {!initialLoadComplete ? (
            <div className='space-y-2'>
              <Skeleton className='h-2 w-full' />
              <div className='flex justify-between'>
                <Skeleton className='h-3 w-24' />
                <Skeleton className='h-3 w-20' />
              </div>
            </div>
          ) : (
            <>
              <Progress value={storageStatus.projectedPercent} className='h-2 mb-2' />
              <div className='flex justify-between text-xs text-slate-500 dark:text-stone-400'>
                <span>
                  {formatBytes(storageStatus.storageUsedBytes, 1000)} {t('upload.used')}
                  {storageStatus.pendingBytes > 0 && (
                    <span className='text-blue-500'>
                      {' '}
                      + {formatBytes(storageStatus.pendingBytes, 1000)} {t('upload.pending')}
                    </span>
                  )}
                </span>
                <span>
                  {formatBytes(storageStatus.storageLimitBytes, 1000)} {t('upload.limit')}
                </span>
              </div>
              {storageStatus.remainingBytes > 0 && (
                <p className='text-xs text-slate-400 dark:text-stone-500 mt-1'>
                  {formatBytes(storageStatus.remainingBytes, 1000)} {t('upload.remaining')}
                </p>
              )}
              {(organization as any)?.ephemeralBytes > 0 && (
                <p className='text-xs text-slate-400 dark:text-stone-500 italic'>
                  {t('upload.includingEphemeral', {
                    size: formatBytes((organization as any).ephemeralBytes, 1000),
                  })}
                </p>
              )}
            </>
          )}
        </div>

        {/* Storage Limit Warning */}
        {initialLoadComplete && storageStatus.wouldExceedLimit && files.length > 0 && (
          <Alert variant='destructive' className='mb-6'>
            <DangerTriangle className='h-4 w-4' />
            <AlertTitle>{t('upload.storageLimitExceeded')}</AlertTitle>
            <AlertDescription>
              {t('upload.storageLimitExceededDescription', {
                size: formatBytes(storageStatus.pendingBytes),
                remaining: formatBytes(storageStatus.remainingBytes),
              })}
            </AlertDescription>
          </Alert>
        )}

        {/* Near Limit Warning */}
        {initialLoadComplete && !storageStatus.wouldExceedLimit && storageStatus.usagePercent >= 90 && (
          <Alert className='mb-6'>
            <DangerTriangle className='h-4 w-4' />
            <AlertTitle>{t('upload.storageAlmostFull')}</AlertTitle>
            <AlertDescription>
              {t('upload.storageAlmostFullDescription', { percent: storageStatus.usagePercent.toFixed(0) })}
            </AlertDescription>
          </Alert>
        )}

        {/* E57 File Limit Warning */}
        {e57Warning.show && (
          <Alert className='mb-6'>
            <DangerTriangle className='h-4 w-4' />
            <AlertTitle>{t('upload.e57LimitWarningTitle')}</AlertTitle>
            <AlertDescription>
              {e57Warning.exceedsCount && e57Warning.exceedsSize
                ? t('upload.e57LimitWarningBoth', {
                    count: e57Warning.e57Count,
                    size: formatBytes(e57Warning.e57TotalBytes),
                  })
                : e57Warning.exceedsCount
                  ? t('upload.e57LimitWarningCount', { count: e57Warning.e57Count })
                  : t('upload.e57LimitWarningSize', { size: formatBytes(e57Warning.e57TotalBytes) })}
            </AlertDescription>
          </Alert>
        )}

        <WorkingFileUploader onFilesAdded={addFiles} />
        {files.length > 0 && (
          <>
            {files.length > 0 && !isUploading && (
              <div className='flex mt-4 items-end gap-4'>
                <div className='flex-1'>
                  <Label>{t('upload.batchLabel')}</Label>
                  <Input
                    className='mt-1'
                    placeholder={t('upload.batchLabelPlaceholder')}
                    value={batchLabel}
                    onChange={(e) => setBatchLabel(e.target.value)}
                    maxLength={255}
                  />
                </div>
                <div className=''>
                  {!isUploading && files.length > 0 && (
                    <Button
                      onClick={() => startUploads({ label: batchLabel || undefined })}
                      disabled={!initialLoadComplete || storageStatus.wouldExceedLimit}>
                      {!initialLoadComplete
                        ? t('edit.loading')
                        : storageStatus.wouldExceedLimit
                          ? t('upload.storageLimitExceeded')
                          : t('upload.startUpload')}
                    </Button>
                  )}
                  {isUploading && (
                    <Button variant='destructive' onClick={cancelUpload}>
                      {t('upload.cancelUpload')}
                    </Button>
                  )}
                </div>
              </div>
            )}
            <Label className='mb-2 mt-4'>{t('upload.uploadedItems', { count: files.length })}</Label>
            <FileList files={files} onRemoveFile={removeFile} onClearAll={clearFiles} />
            <div className='h-8' />
          </>
        )}

        <ErrorBoundary fallback={<div>{t('upload.error')}</div>}>
          <div id='onboarding-batch'>
            <BatchList />
          </div>
        </ErrorBoundary>
      </div>
    </PageSkeleton>
  );
};

export default index;
